import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import ThemeToggle from './ThemeToggle'
import Logo from './Logo'
import CalendlyButton from './CalendlyButton'

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false)
  const location = useLocation()

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Services', href: '/services' },
    { name: 'About', href: '/about' },
    // { name: 'News', href: '/news' }, // Hidden for now
    { name: 'Careers', href: '/careers' },
    { name: 'Contact', href: '/contact' },
  ]

  const isActive = (path: string) => location.pathname === path

  /*
   * The bar is opaque, with no backdrop filter, on purpose.
   *
   * iOS Safari tints the strip behind its status bar by sampling a fixed,
   * near-full-width element flush against the top edge, but it refuses to read
   * one that is translucent or carries a backdrop-filter, and falls back to the
   * page canvas instead. That fallback is the problem: the canvas is a single
   * colour and is also what the footer end needs, and the footer's tone
   * alternates per route. Being sampleable here is what lets the two ends of the
   * screen be different colours at all.
   *
   * The visual cost is 5% translucency behind a 4px blur, which was already close
   * to invisible. See src/index.css and components/CanvasTone. A unit test guards
   * the opacity, because losing it silently turns the top strip ashy.
   */
  return (
    <nav className="fixed top-0 left-0 w-full bg-white dark:bg-gray-900 shadow-sm z-50 transition-colors duration-200">
      <div className="container-custom">
        <div className="relative flex justify-between items-center py-4">
          {/* Mobile: Theme toggle on left */}
          <div className="flex items-center md:hidden">
            <ThemeToggle />
          </div>

          {/* Logo - Centered on mobile, left on desktop */}
          <div className="flex-1 flex justify-center md:justify-start">
            <Link to="/" className="flex items-center">
              <Logo className="h-8" />
            </Link>
          </div>

          {/* Navigation - Center */}
          <div className="hidden md:flex items-center justify-center flex-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`relative px-4 py-2 text-base transition-all duration-200 ${
                  isActive(item.href)
                    ? 'text-primary-600 font-semibold'
                    : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 font-regular hover:font-semibold'
                }`}
                aria-current={isActive(item.href) ? 'page' : undefined}
              >
                {item.name}
                {isActive(item.href) && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600"
                    initial={false}
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
              </Link>
            ))}
          </div>

          {/* Actions - Right side on desktop */}
          <div className="hidden md:flex items-center justify-end space-x-4 flex-1">
            <ThemeToggle />
            <CalendlyButton>
              Get Started
            </CalendlyButton>
          </div>

          {/* Mobile menu button - Right side */}
          <div className="flex items-center md:hidden">
            <button
              className="text-gray-700 dark:text-gray-300"
              onClick={() => setIsOpen(!isOpen)}
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isOpen}
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden py-6 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-lg"
          >
            <div className="flex flex-col space-y-4 items-center text-center">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`px-3 py-2 text-base font-medium ${
                    isActive(item.href)
                      ? 'text-primary-600'
                      : 'text-gray-700 dark:text-gray-300 hover:text-primary-600'
                  }`}
                  onClick={() => setIsOpen(false)}
                  aria-current={isActive(item.href) ? 'page' : undefined}
                >
                  {item.name}
                </Link>
              ))}
              <div onClick={() => setIsOpen(false)} className="pt-4">
                <CalendlyButton className="w-fit">
                  Get Started
                </CalendlyButton>
              </div>
            </div>
            {/* Bottom border with gradient shadow */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-b from-gray-200 dark:from-gray-700 to-transparent"></div>
          </motion.div>
        )}
      </div>
    </nav>
  )
}

export default Navbar